'use client';

import { useMemo, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { UploadFile } from '@/app/types/file';
import {
  ManualUploadCsvRow,
  ManualUploadFileSummary,
} from '@/app/types/manual-upload';
import {
  uploadManualArchive,
  uploadManualFileInChunks,
  uploadMappedCsvRows,
} from '@/app/services/manualUpload/manual-upload.service';

import {
  SUPPORTED_EXTENSIONS,
  ARCHIVE_EXTENSIONS,
} from '@evimo/types/constants/file-extensions';

type UseManualUploadOptions = {
  initialAssetId?: string;
  initialClientName?: string;
};

const MAX_FILE_SIZE_BYTES = 500 * 1024 * 1024;

const hasSupportedExtension = (fileName: string): boolean => {
  const normalizedName = fileName.toLowerCase();
  return SUPPORTED_EXTENSIONS.some((extension) =>
    normalizedName.endsWith(extension),
  );
};

const isArchiveFile = (fileName: string): boolean => {
  const normalizedName = fileName.toLowerCase();
  return ARCHIVE_EXTENSIONS.some((extension) =>
    normalizedName.endsWith(extension),
  );
};

const toCsvRows = (
  rows: Array<Record<string, string>>,
): ManualUploadCsvRow[] => {
  return rows.map((row) => ({
    timestamp: row.timestamp ?? '',
    log: row.log ?? '',
  }));
};

export const useManualUpload = (options?: UseManualUploadOptions) => {
  const { t } = useTranslation('manualUpload');
  const [assetId, setAssetId] = useState(options?.initialAssetId ?? '');
  const [clientName] = useState(options?.initialClientName ?? '');
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [activePreview, setActivePreview] = useState<UploadFile | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ManualUploadFileSummary | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmittingCsv, setIsSubmittingCsv] = useState(false);

  const validateFields = (): boolean => {
    if (!assetId.trim()) {
      const errorMessage = t('assetIdRequired');
      setGlobalError(errorMessage);
      message.error(errorMessage);
      return false;
    }

    if (!clientName.trim()) {
      const errorMessage = t('clientNameRequired');
      setGlobalError(errorMessage);
      message.error(errorMessage);
      return false;
    }

    setGlobalError(null);
    return true;
  };

  const addFiles = (selectedFiles: File[]): void => {
    const nextFiles: UploadFile[] = [];
    const errors: string[] = [];

    selectedFiles.forEach((file) => {
      if (!hasSupportedExtension(file.name)) {
        errors.push(`${file.name}: ${t('unsupportedFileFormat')}`);
        return;
      }

      if (file.size === 0) {
        errors.push(`${file.name}: ${t('fileIsEmpty')}`);
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        errors.push(`${file.name}: ${t('fileTooLarge')}`);
        return;
      }

      nextFiles.push({
        id: `${file.name}-${file.size}-${file.lastModified}-${crypto.randomUUID()}`,
        file,
        progress: 0,
        status: 'queued',
      });
    });

    if (errors.length > 0) {
      setGlobalError(errors.join('\n'));
      message.error(t('someFilesRejected'));
    } else {
      setGlobalError(null);
    }

    if (nextFiles.length > 0) {
      setFiles((currentFiles) => [...currentFiles, ...nextFiles]);
      message.success(t('filesAddedToQueue', { count: nextFiles.length }));
    }
  };

  const removeFile = (fileId: string): void => {
    setFiles((currentFiles) =>
      currentFiles.filter((file) => file.id !== fileId),
    );
    setActivePreview((currentPreview) =>
      currentPreview?.id === fileId ? null : currentPreview,
    );
  };

  const startUpload = async (): Promise<void> => {
    if (files.length === 0 || !validateFields()) {
      return;
    }

    setIsUploading(true);
    setSummary(null);

    for (const fileEntry of files) {
      setFiles((currentFiles) =>
        currentFiles.map((currentFile) =>
          currentFile.id === fileEntry.id
            ? {
                ...currentFile,
                status: 'uploading',
                progress: 0,
                error: undefined,
              }
            : currentFile,
        ),
      );

      try {
        const uploadManualFile = isArchiveFile(fileEntry.file.name)
          ? uploadManualArchive
          : uploadManualFileInChunks;
        const nextSummary = await uploadManualFile({
          file: fileEntry.file,
          assetId,
          clientName,
          onProgress: (progress) => {
            setFiles((currentFiles) =>
              currentFiles.map((currentFile) =>
                currentFile.id === fileEntry.id
                  ? { ...currentFile, progress }
                  : currentFile,
              ),
            );
          },
        });

        setFiles((currentFiles) =>
          currentFiles.map((currentFile) =>
            currentFile.id === fileEntry.id
              ? { ...currentFile, status: 'done', progress: 100 }
              : currentFile,
          ),
        );
        setSummary(nextSummary);

        if (nextSummary.failedCount > 0) {
          message.warning(t('uploadCompletedWithWarnings'));
        } else {
          message.success(t('uploadCompletedSuccessfully'));
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : t('uploadFailed');

        setFiles((currentFiles) =>
          currentFiles.map((currentFile) =>
            currentFile.id === fileEntry.id
              ? {
                  ...currentFile,
                  status: 'error',
                  progress: 0,
                  error: errorMessage,
                }
              : currentFile,
          ),
        );
        setGlobalError(errorMessage);
        message.error(errorMessage);
      }
    }

    setIsUploading(false);
  };

  const submitCsvRows = async (
    rows: Array<Record<string, string>>,
  ): Promise<void> => {
    if (!validateFields()) {
      return;
    }

    setIsSubmittingCsv(true);
    try {
      const nextSummary = await uploadMappedCsvRows({
        assetId,
        clientName,
        sourceName: 'manual_upload_matchy.csv',
        rows: toCsvRows(rows),
      });
      setSummary(nextSummary);

      if (nextSummary.failedCount > 0) {
        message.warning(t('uploadCompletedWithWarnings'));
      } else {
        message.success(t('csvRowsImported'));
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : t('uploadFailed');
      setGlobalError(errorMessage);
      message.error(errorMessage);
    } finally {
      setIsSubmittingCsv(false);
    }
  };

  const supportedFormatsLabel = useMemo(
    () => SUPPORTED_EXTENSIONS.join(', '),
    [],
  );

  return {
    t,
    assetId,
    setAssetId,
    files,
    activePreview,
    setActivePreview,
    globalError,
    setGlobalError,
    summary,
    isUploading,
    isSubmittingCsv,
    supportedFormatsLabel,
    addFiles,
    removeFile,
    startUpload,
    submitCsvRows,
  };
};
